import { useState, useCallback } from 'react';
import { useModal } from '../../../context/ModalContext';
import styles from './ROICalculator.module.css';

const EMPTY_TASK = { name: '', hoursPerWeek: 10, rateZar: 350 };

const ROICalculator = () => {
  const { openModal } = useModal();
  const [tasks, setTasks] = useState([{ ...EMPTY_TASK, name: 'Data entry & reporting' }]);

  const updateTask = useCallback((i, field, value) => {
    setTasks(prev => prev.map((t, idx) => idx === i ? { ...t, [field]: value } : t));
  }, []);

  const addTask = useCallback(() => {
    if (tasks.length < 3) setTasks(prev => [...prev, { ...EMPTY_TASK }]);
  }, [tasks.length]);

  const removeTask = useCallback(i => {
    setTasks(prev => prev.filter((_, idx) => idx !== i));
  }, []);

  const totalHoursPerWeek = tasks.reduce((s, t) => s + t.hoursPerWeek, 0);
  const totalWeeklyCost = tasks.reduce((s, t) => s + t.hoursPerWeek * t.rateZar, 0);
  const hoursPerYear = totalHoursPerWeek * 52;
  const savingsPerYear = Math.round(totalWeeklyCost * 52 * 0.9);
  const fmtZar = n => `R${n.toLocaleString('en-ZA')}`;

  return (
    <div className={styles.wrap}>
      <div className={styles.body}>
        {/* Tasks */}
        <div className={styles.tasks}>
          {tasks.map((task, i) => (
            <div key={i} className={styles.task}>
              <div className={styles.taskHeader}>
                <span className={styles.taskNum}>Task {i + 1}</span>
                {tasks.length > 1 && (
                  <button className={styles.removeBtn} onClick={() => removeTask(i)} aria-label="Remove task">
                    Remove
                  </button>
                )}
              </div>
              <input
                className={styles.taskName}
                type="text"
                placeholder="e.g. Data entry and reporting"
                value={task.name}
                onChange={e => updateTask(i, 'name', e.target.value)}
              />
              <div className={styles.sliders}>
                <div className={styles.sliderGroup}>
                  <div className={styles.sliderLabel}>
                    <span>Hours per week</span>
                    <strong>{task.hoursPerWeek}h</strong>
                  </div>
                  <input
                    type="range" min={1} max={40} step={1}
                    value={task.hoursPerWeek}
                    onChange={e => updateTask(i, 'hoursPerWeek', Number(e.target.value))}
                    className={styles.slider}
                  />
                  <div className={styles.sliderTicks}>
                    <span>1h</span><span>20h</span><span>40h</span>
                  </div>
                </div>
                <div className={styles.sliderGroup}>
                  <div className={styles.sliderLabel}>
                    <span>Hourly rate (ZAR)</span>
                    <strong>{fmtZar(task.rateZar)}/hr</strong>
                  </div>
                  <input
                    type="range" min={100} max={2000} step={50}
                    value={task.rateZar}
                    onChange={e => updateTask(i, 'rateZar', Number(e.target.value))}
                    className={styles.slider}
                  />
                  <div className={styles.sliderTicks}>
                    <span>R100</span><span>R1 050</span><span>R2 000</span>
                  </div>
                </div>
              </div>
              <div className={styles.taskCost}>
                Weekly cost: <strong>{fmtZar(task.hoursPerWeek * task.rateZar)}</strong>
              </div>
            </div>
          ))}

          {tasks.length < 3 && (
            <button className={styles.addBtn} onClick={addTask}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
              </svg>
              Add another task
            </button>
          )}
        </div>

        {/* Results panel */}
        <div className={styles.results}>
          <p className={styles.resultsLabel}>If fully automated</p>
          <div className={styles.resultCards}>
            <div className={styles.resultCard}>
              <span className={styles.resultValue}>{hoursPerYear.toLocaleString('en-ZA')}</span>
              <span className={styles.resultUnit}>hours saved per year</span>
            </div>
            <div className={`${styles.resultCard} ${styles.resultCardAccent}`}>
              <span className={styles.resultValue}>{fmtZar(savingsPerYear)}</span>
              <span className={styles.resultUnit}>saved per year</span>
            </div>
          </div>
          <p className={styles.assumption}>
            Based on {totalHoursPerWeek}h/week across {tasks.length} task{tasks.length > 1 ? 's' : ''}, at 90% automation efficiency.
          </p>

          <div className={styles.divider} />

          <p className={styles.ctaText}>
            Ready to get that time back?
          </p>
          <button className={styles.ctaBtn} onClick={openModal}>
            Let's build it →
          </button>
        </div>
      </div>
    </div>
  );
};

export default ROICalculator;
